import type { ReactNode } from "react";
import { createContext, useContext } from "react";

export type WidgetRuntimeConfig = {
  apiKey: string;
  apiUrl: string;
  showChatQuestions: boolean;
  showLandingQuestions: boolean;
  organizationSlug?: string;
  widgetDisclaimerText?: string;
  widgetSuggestedQuestions?: string[];
  placeholderText?: string;
  langfuse?: {
    baseUrl: string;
    publicKey: string;
    environment?: string;
  } | null;
};

type WidgetRuntimeConfigProviderProps = {
  value: Omit<WidgetRuntimeConfig, "showChatQuestions" | "showLandingQuestions">
    & Partial<Pick<WidgetRuntimeConfig, "showChatQuestions" | "showLandingQuestions">>;
  children: ReactNode;
};

const WidgetRuntimeConfigContext = createContext<WidgetRuntimeConfig | null>(null);

export const WidgetRuntimeConfigProvider = ({
  value,
  children,
}: WidgetRuntimeConfigProviderProps) => {
  const runtimeConfig: WidgetRuntimeConfig = {
    ...value,
    showChatQuestions: value.showChatQuestions ?? true,
    showLandingQuestions: value.showLandingQuestions ?? true,
  };

  return (
    <WidgetRuntimeConfigContext value={runtimeConfig}>
      {children}
    </WidgetRuntimeConfigContext>
  );
};

/**
 * Strict accessor — throws when the context is missing.
 * Use inside components that are always rendered under WidgetRuntimeConfigProvider
 * (i.e. anything inside an api-key-bound web component).
 */
export const useWidgetRuntimeConfig = (): WidgetRuntimeConfig => {
  const config = useContext(WidgetRuntimeConfigContext);
  if (!config) {
    throw new Error("Widget runtime config is missing.");
  }
  return config;
};

/**
 * Lenient accessor — returns null when the context is missing.
 * Use inside components that may render outside a provider (e.g. dev components).
 */
export const useOptionalWidgetRuntimeConfig = (): WidgetRuntimeConfig | null => {
  return useContext(WidgetRuntimeConfigContext);
};
